/**
 * Patch global fetch for Datadog request body capture (primarily for Fern SDK).
 *
 * Why:
 * - Datadog RUM "Resources" does not include request bodies.
 * - Fern SDK uses `fetch` under the hood, so this lets us attach request bodies
 *   (redacted + truncated) to Datadog Logs + RUM when a request fails.
 *
 * Safety:
 * - Only enabled in production (non-test Stytch env).
 * - Only logs non-GET requests.
 * - Redacts common sensitive fields.
 * - Truncates to keep event sizes bounded.
 */

import { getRedactedRequestBody } from './datadogRequestCapture';
import { logError } from './error';
import { markErrorAsLogged } from './fetchErrorTracker';

const shouldCaptureRequestBodyForDatadog = process.env.NEXT_PUBLIC_STYTCH_PROJECT_ENV !== 'test';
const theraBackendURL = process.env.NEXT_PUBLIC_BACKEND_URL;

/**
 * Only capture request bodies for our own backend.
 * We should NOT attempt to read or log payloads for 3rd-party requests (Datadog, Posthog, Intercom, etc.).
 */
const shouldCaptureForUrl = (url: string): boolean => {
  if (!theraBackendURL) return false;
  return url.startsWith(theraBackendURL);
};

/**
 * Read request body from a `Request` object (when SDKs call `fetch(new Request(...))`).
 *
 * We only attempt to read *text-like* bodies for safety.
 * Anything else (streams, binaries) is skipped.
 */
const readRequestBodyTextFromRequest = async (request: Request): Promise<string | undefined> => {
  try {
    const contentType = request.headers.get('content-type') || '';
    const isTextLike =
      contentType.includes('application/json') ||
      contentType.includes('application/x-www-form-urlencoded') ||
      contentType.includes('text/');
    if (!isTextLike) return undefined;

    // Clone so we don't consume the original request body.
    const clone = request.clone();
    const text = await clone.text();
    return text || undefined;
  } catch {
    return undefined;
  }
};

export const patchFetchForDatadog = (): void => {
  if (!shouldCaptureRequestBodyForDatadog) return;
  if (typeof window === 'undefined') return;
  if (!theraBackendURL) return;

  const w = window as any;
  if (w.__theraFetchDatadogPatched) return;
  w.__theraFetchDatadogPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : (input as any)?.url?.toString?.() || input.toString();
    const method = (init?.method || (input as any)?.method || 'GET').toString().toUpperCase();
    const requestBody = init?.body;

    try {
      const response = await originalFetch(input, init);

      // Only report failed responses (avoid huge volume).
      if (!response.ok && method !== 'GET' && shouldCaptureForUrl(url)) {
        // Skip auth errors to reduce noise and avoid logging auth payloads.
        if (response.status !== 401 && response.status !== 403) {
          const requestBodyText =
            requestBody === undefined && typeof Request !== 'undefined' && input instanceof Request
              ? await readRequestBodyTextFromRequest(input)
              : undefined;
          const { body, bodyTruncated } = getRedactedRequestBody(requestBodyText ?? requestBody);
          logError(new Error(`Fetch failed: ${method} ${url}`), {
            source: 'Fetch',
            category: 'api',
            data: {
              httpStatus: response.status,
              api_method: method,
              api_url: url,
              ...(body !== undefined ? { api_request_body: body } : {}),
              ...(bodyTruncated !== undefined ? { api_request_body_truncated: bodyTruncated } : {}),
            },
          });
        }
      }

      return response;
    } catch (error) {
      // Network / CORS / abort errors land here (no status code).
      if (method !== 'GET' && shouldCaptureForUrl(url)) {
        const requestBodyText =
          requestBody === undefined && typeof Request !== 'undefined' && input instanceof Request
            ? await readRequestBodyTextFromRequest(input)
            : undefined;
        const { body, bodyTruncated } = getRedactedRequestBody(requestBodyText ?? requestBody);
        logError(error as Error, {
          source: 'Fetch',
          category: 'api',
          data: {
            api_method: method,
            api_url: url,
            ...(body !== undefined ? { api_request_body: body } : {}),
            ...(bodyTruncated !== undefined ? { api_request_body_truncated: bodyTruncated } : {}),
          },
        });
        // Mark error as logged AFTER logging to prevent double-logging by calling code.
        if (error instanceof Error) {
          markErrorAsLogged(error);
        }
      }
      throw error;
    }
  };
};
